export const RUNTIME_OBSERVER_METHODS = {
  observerSubscribe: "observer.subscribe",
  approvalApprove: "approval.approve",
  approvalReject: "approval.reject",
} as const;

export const RUNTIME_OBSERVER_CHANNELS = {
  serverWelcome: "server.welcome",
  observerConnected: "observer.connected",
  observerDisconnected: "observer.disconnected",
  executionQueued: "execution.queued",
  approvalRequested: "approval.requested",
  approvalResolved: "approval.resolved",
  executionStarted: "execution.started",
  executionCompleted: "execution.completed",
  executionFailed: "execution.failed",
} as const;

type RuntimeExecutionSource = "human-ui" | "cli" | "mcp";
type RuntimeExecutionRequestType = "graphql" | "rest" | "mongo" | "sql";
type RuntimeApprovalMode = "none" | "required";
type RuntimeExecutionIntent = "read" | "write" | "unknown";
type ObserverCapability = "observe" | "approve";

type RuntimeExecutionLifecycleEvent = {
  executionId: string;
  sessionId?: string;
};

type RuntimeExecutionMetaEvent = RuntimeExecutionLifecycleEvent & {
  source: RuntimeExecutionSource;
  initiator: RuntimeExecutionSource;
  requestType: RuntimeExecutionRequestType;
  intent: RuntimeExecutionIntent;
  approvalMode: RuntimeApprovalMode;
};

export type RuntimeObserverEvent =
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.serverWelcome;
      data: { version: string };
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.observerConnected;
      data: { observerId: string; observerName: string; capabilities: ObserverCapability[] };
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.observerDisconnected;
      data: { observerId: string; reason?: string };
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.executionQueued;
      data: RuntimeExecutionMetaEvent;
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.approvalRequested;
      data: RuntimeExecutionMetaEvent;
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.approvalResolved;
      data:
        | (RuntimeExecutionMetaEvent & { decision: "approved"; observerId: string })
        | (RuntimeExecutionMetaEvent & { decision: "rejected"; observerId: string; reason?: string })
        | (RuntimeExecutionMetaEvent & { decision: "timeout"; reason?: string });
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.executionStarted;
      data: RuntimeExecutionLifecycleEvent;
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.executionCompleted;
      data: RuntimeExecutionLifecycleEvent & { statusCode: number; durationMs: number };
    }
  | {
      type: "push";
      sequence: number;
      channel: typeof RUNTIME_OBSERVER_CHANNELS.executionFailed;
      data: RuntimeExecutionLifecycleEvent & { errorMessage: string };
    };

export interface RuntimeObserverSocket {
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: (() => void) | null;
  onerror: (() => void) | null;
  send(payload: string): void;
  close(): void;
}

export interface RuntimeObserverClient {
  readonly socket: RuntimeObserverSocket | null;
  connect(): void;
  approve(executionId: string): void;
  reject(executionId: string, reason: string): void;
  disconnect(): void;
}

function decodeSocketMessage(data: unknown): string | null {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return new TextDecoder().decode(new Uint8Array(data));
  if (data instanceof Uint8Array) return new TextDecoder().decode(data);
  return null;
}

export function createRuntimeObserverClient(input: {
  runtimeWsUrl: string;
  observerId: string;
  observerName: string;
  capabilities: ObserverCapability[];
  onEvent: (event: RuntimeObserverEvent) => void;
  onConnectionChange?: (connected: boolean) => void;
  socketFactory?: (url: string) => RuntimeObserverSocket;
}): RuntimeObserverClient {
  let socket: RuntimeObserverSocket | null = null;
  let sequence = 0;
  let connected = false;

  const setConnected = (nextConnected: boolean) => {
    if (connected === nextConnected) return;
    connected = nextConnected;
    input.onConnectionChange?.(nextConnected);
  };

  const send = (prefix: string, body: unknown) => {
    if (!socket) return;
    socket.send(JSON.stringify({
      id: `${prefix}-${++sequence}`,
      body,
    }));
  };

  return {
    get socket() {
      return socket;
    },

    connect() {
      if (socket) return;

      const factory = input.socketFactory ?? ((url: string) => new WebSocket(url) as unknown as RuntimeObserverSocket);
      const nextSocket = factory(input.runtimeWsUrl);
      socket = nextSocket;

      nextSocket.onopen = () => {
        if (socket !== nextSocket) return;
        setConnected(true);
        send("observer-subscribe", {
          _tag: RUNTIME_OBSERVER_METHODS.observerSubscribe,
          observerId: input.observerId,
          observerName: input.observerName,
          capabilities: input.capabilities,
        });
      };

      nextSocket.onmessage = (message) => {
        if (socket !== nextSocket) return;
        const text = decodeSocketMessage(message.data);
        if (!text) return;

        let parsed: unknown;
        try {
          parsed = JSON.parse(text);
        } catch {
          return;
        }

        if (
          parsed
          && typeof parsed === "object"
          && (parsed as { type?: unknown }).type === "push"
          && typeof (parsed as { channel?: unknown }).channel === "string"
        ) {
          input.onEvent(parsed as RuntimeObserverEvent);
        }
      };

      nextSocket.onclose = () => {
        if (socket !== nextSocket) return;
        socket = null;
        setConnected(false);
      };

      nextSocket.onerror = () => {
        if (socket !== nextSocket) return;
        setConnected(false);
      };
    },

    approve(executionId: string) {
      send("approval-approve", {
        _tag: RUNTIME_OBSERVER_METHODS.approvalApprove,
        executionId,
        observerId: input.observerId,
      });
    },

    reject(executionId: string, reason: string) {
      send("approval-reject", {
        _tag: RUNTIME_OBSERVER_METHODS.approvalReject,
        executionId,
        observerId: input.observerId,
        reason,
      });
    },

    disconnect() {
      const currentSocket = socket;
      socket = null;
      setConnected(false);
      currentSocket?.close();
    },
  };
}
